import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ShieldCheck,
  Stethoscope,
  UserSearch,
  Scale,
  GraduationCap,
  TrendingUp,
  Check,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'

// Framework data with icons and details
const FRAMEWORKS = [
  {
    id: 'hipaa',
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    description: 'Required for processing protected health information (PHI) in healthcare settings.',
    icon: Stethoscope,
    requiredFor: ['clinical'],
    requirements: [
      'Covered Entity or Business Associate certification',
      'Business Associate Agreement acknowledgment',
      'HIPAA compliance program attestation',
    ],
  },
  {
    id: 'eeoc',
    name: 'EEOC',
    fullName: 'Equal Employment Opportunity Commission',
    description: 'Required for fair hiring practices and employment-related personality assessments.',
    icon: UserSearch,
    requiredFor: ['talent'],
    requirements: [
      'Fair hiring practices commitment',
      'Adverse impact monitoring agreement',
      'Non-discrimination policy acknowledgment',
    ],
  },
  {
    id: 'batson',
    name: 'Batson',
    fullName: 'Batson v. Kentucky Compliance',
    description: 'Required for jury selection analysis to prevent discriminatory practices.',
    icon: Scale,
    requiredFor: ['legal'],
    requirements: [
      'Methodology acknowledgment',
      'Human-in-loop commitment',
      'Expert review agreement',
    ],
  },
  {
    id: 'ferpa',
    name: 'FERPA',
    fullName: 'Family Educational Rights and Privacy Act',
    description: 'Required for processing student education records and data.',
    icon: GraduationCap,
    requiredFor: ['education'],
    requirements: [
      'Educational institution certification',
      'Student data protection commitment',
      'Parental consent process acknowledgment',
    ],
  },
  {
    id: 'finra',
    name: 'FINRA',
    fullName: 'Financial Industry Regulatory Authority',
    description: 'Required for behavioral analysis in financial services and investment advice.',
    icon: TrendingUp,
    requiredFor: ['finance'],
    requirements: [
      'Registered representative certification',
      'Suitability documentation commitment',
      'Compliance program acknowledgment',
    ],
  },
]

// Map products to required frameworks
const PRODUCT_FRAMEWORK_MAP: Record<string, string[]> = {
  clinical: ['hipaa'],
  talent: ['eeoc'],
  legal: ['batson'],
  education: ['ferpa'],
  finance: ['finra'],
  engage: [],
  coach: [],
  identity: [],
}

export default function FrameworkSelection() {
  const navigate = useNavigate()
  const { selectedProducts, setStep, attestationData, setAttestationData } = useOnboardingStore()
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(
    attestationData?.selectedFrameworks || []
  )

  useEffect(() => {
    setStep(5)
  }, [setStep])

  // Determine which frameworks are required and which are optional
  const requiredFrameworks = Array.from(
    new Set(
      selectedProducts.flatMap((productId) => PRODUCT_FRAMEWORK_MAP[productId] || [])
    )
  )

  // Filter frameworks to show only relevant ones
  const relevantFrameworks = FRAMEWORKS.filter((f) =>
    requiredFrameworks.includes(f.id)
  )

  const handleToggleFramework = (frameworkId: string) => {
    setSelectedFrameworks((prev) =>
      prev.includes(frameworkId)
        ? prev.filter((id) => id !== frameworkId)
        : [...prev, frameworkId]
    )
  }

  const handleContinue = () => {
    if (selectedFrameworks.length === 0) {
      // Skip to invite team if none selected
      navigate('/onboarding/invite-team')
      return
    }

    // Save selected frameworks to store
    setAttestationData({
      selectedFrameworks,
      frameworkAttestations: attestationData?.frameworkAttestations || {},
      currentFrameworkIndex: 0,
    })

    // Navigate to first framework form
    navigate(`/onboarding/attestation/form/${selectedFrameworks[0]}`)
  }

  const handleBack = () => {
    navigate('/onboarding/attestation')
  }

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-copper-100 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-copper-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900">
            Select Frameworks
          </h1>
        </div>
        <p className="text-warm-600">
          Choose which regulatory frameworks you'd like to attest to now
        </p>
      </motion.div>

      {/* Framework Cards */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4 mb-8"
      >
        {relevantFrameworks.map((framework, index) => {
          const Icon = framework.icon
          const isSelected = selectedFrameworks.includes(framework.id)
          const isRequired = requiredFrameworks.includes(framework.id)

          return (
            <motion.div
              key={framework.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <button
                type="button"
                onClick={() => handleToggleFramework(framework.id)}
                className={`w-full p-5 rounded-xl text-left border-2 transition-all duration-200 ${
                  isSelected
                    ? 'bg-copper-50 border-copper-500'
                    : 'bg-white border-warm-200 hover:border-copper-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Selection Indicator */}
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all ${
                      isSelected
                        ? 'bg-copper-500 border-copper-500'
                        : 'bg-white border-warm-300'
                    }`}
                  >
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-copper-500 text-white'
                        : 'bg-midnight-100 text-midnight-600'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading font-semibold text-midnight-900">
                        {framework.name}
                      </h3>
                      {isRequired && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-heading">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-warm-500 mb-2">{framework.fullName}</p>
                    <p className="text-sm text-warm-600 mb-3">{framework.description}</p>

                    {/* Requirements Preview */}
                    <div className="pt-3 border-t border-warm-100">
                      <p className="text-xs font-heading font-semibold text-midnight-700 mb-2">
                        Attestation Requirements:
                      </p>
                      <ul className="text-xs text-warm-600 space-y-1">
                        {framework.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="w-1 h-1 rounded-full bg-warm-400 mt-1.5 flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </button>
            </motion.div>
          )
        })}
      </motion.div>

      {/* Selection Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        {selectedFrameworks.length > 0 ? (
          <p className="text-sm text-warm-600 text-center">
            You've selected{' '}
            <span className="font-heading font-semibold text-copper-600">
              {selectedFrameworks.length}{' '}
              {selectedFrameworks.length === 1 ? 'framework' : 'frameworks'}
            </span>{' '}
            to attest to
          </p>
        ) : (
          <p className="text-sm text-warm-500 text-center">
            Select at least one framework to continue with attestation,
            or skip to complete later
          </p>
        )}
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <button onClick={handleBack} className="btn btn-secondary flex items-center justify-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleContinue}
          className="flex-1 btn btn-primary flex items-center justify-center gap-2"
        >
          {selectedFrameworks.length > 0 ? (
            <>
              Continue to Attestation
              <ArrowRight className="w-4 h-4" />
            </>
          ) : (
            <>
              Skip Attestation
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </motion.div>
    </div>
  )
}
